import { useMemo } from "react";
import ChartDataLabels from "chartjs-plugin-datalabels";
import PropTypes from "prop-types";
import { GlobalStyles } from "@mui/material";

// react-chartjs-2 components
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// ReportsBarChart configurations
import configs from "examples/Charts/BarCharts/ReportsBarChart/configs";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function WeightChart({
  color,
  title,
  chart,
  cycle_day,
  weight_average,
  weight_target,
  timestamp,
  placement_weight,
  weight_message,
  weight_status,
}) {
  const { data, options } = configs(chart.labels || [], chart.datasets || {});

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox padding="1rem">
        {useMemo(
          () => (
            <MDBox
              variant="gradient"
              bgColor={color}
              borderRadius="lg"
              coloredShadow={color}
              py={2}
              pr={0.5}
              mt={-5}
              height="12.5rem"
            >
              <Bar data={data} options={options} redraw />
            </MDBox>
          ),
          [color, chart]
        )}
        <MDBox pt={3} pb={1} px={1}>
          <MDTypography variant="h6" textTransform="capitalize">
            {title}
          </MDTypography>
          <MDTypography variant="h6" textTransform="capitalize">
            Day {cycle_day}
          </MDTypography>
        </MDBox>

        <GlobalStyles
          styles={`
            @keyframes scroll-left {
              0% { transform: translateX(100%); }
              100% { transform: translateX(-100%); }
            }
          `}
        />

        <MDBox
          position="absolute"
          bottom={25}
          right={16}
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          gap={0.5}
        >
          {weight_average != null && (
            <MDBox display="flex" alignItems="center" sx={{ width: "100%" }}>
              <MDTypography sx={{ fontSize: "1rem", minWidth: "65px" }} color="textSecondary">
                Average:
              </MDTypography>
              <MDBox
                component="span"
                sx={{
                  px: 1.2,
                  py: 0.2,
                  mx: 0.5,
                  borderRadius: "4px",
                  backgroundColor: (theme) => {
                    switch ((weight_status || "").toLowerCase()) {
                      case "green":
                        return theme.palette.success.main;
                      case "amber":
                        return theme.palette.warning.main;
                      case "red":
                        return theme.palette.error.main;
                      default:
                        return theme.palette.grey[400];
                    }
                  },
                  color: (theme) => theme.palette.common.white,
                  fontWeight: "bold",
                  fontSize: "1rem",
                  ml: "auto", // pushes to right
                }}
              >
                {weight_average}kg
              </MDBox>
            </MDBox>
          )}

          <MDBox display="flex" flexDirection="column" sx={{ width: "100%" }}>
            {weight_target != null && (
              <MDBox display="flex" alignItems="center" sx={{ width: "100%" }}>
                <MDTypography sx={{ fontSize: "1rem", minWidth: "65px" }} color="textSecondary">
                  Target:
                </MDTypography>
                <MDTypography sx={{ fontSize: "1rem", ml: "auto" }} color="textSecondary">
                  {weight_target}kg
                </MDTypography>
              </MDBox>
            )}

            {placement_weight != null ? (
              <MDBox display="flex" alignItems="center" sx={{ width: "100%" }}>
                <MDTypography sx={{ fontSize: "1rem", minWidth: "65px" }} color="textSecondary">
                  D0:
                </MDTypography>
                <MDTypography sx={{ fontSize: "1rem", ml: "auto" }} color="textSecondary">
                  {placement_weight}kg
                </MDTypography>
              </MDBox>
            ) : (
              weight_message && (
                <MDBox
                  sx={{
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    maxWidth: "180px",
                    ml: "auto",
                    position: "relative",
                  }}
                >
                  <MDTypography
                    sx={{
                      display: "inline-block",
                      fontSize: "1rem",
                      color: "text.error",
                      animation: "scroll-left 10s linear infinite",
                    }}
                  >
                    {weight_message}
                  </MDTypography>
                  <style>
                    {`
                      @keyframes scroll-left {
                        0% { transform: translateX(100%); }
                        100% { transform: translateX(-100%); }
                      }
                    `}
                  </style>
                </MDBox>
              )
            )}
          </MDBox>
        </MDBox>

        <MDBox position="absolute" bottom={8} left={16} display="flex" alignItems="center">
          <MDTypography variant="button" color="text" lineHeight={1} sx={{ mt: 0.15, mr: 0.5 }}>
            <Icon>schedule</Icon>
          </MDTypography>
          <MDTypography variant="button" color="text" fontWeight="light">
            {timestamp}
          </MDTypography>
        </MDBox>
      </MDBox>
    </Card>
  );
}

// Setting default values for the props of ReportsBarChart
WeightChart.defaultProps = {
  color: "info",
  description: "",
};

// Typechecking props for the ReportsBarChart
WeightChart.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  chart: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
  cycle_day: PropTypes.number,
  weight_average: PropTypes.number,
  weight_target: PropTypes.number,
  timestamp: PropTypes.string,
  placement_weight: PropTypes.number,
  weight_message: PropTypes.string,
  weight_status: PropTypes.string,
};

export default WeightChart;
